import { AuthenticationApi, CognitoAuthenticationResult, Configuration, LoginAccount200Response } from '../clients'
import { LocalStorageService } from './localStorageService'

interface StandardAuthService {
  authenticateSilently: () => Promise<ReturnType<LocalStorageService['getAuthenticationData']>>
  passwordLogin: (email: string, password: string, rememberMe: boolean, recaptchaToken: string) => Promise<LoginAccount200Response>
}

type AccessTokenPayload = {
  auth_time: number
  client_id: string
  device_key: string
  event_id: string
  exp: number
  iat: number
  iss: string
  jti: string
  origin_jti: string
  scope: string
  sub: string
  token_use: 'access'
  username: string
}

type IdTokenPayload = {
  aud: string
  auth_time: number
  'cognito:username': string
  email: string
  event_id: string
  exp: number
  iat: number
  iss: string
  jti: string
  origin_jti: string
  phone_number: string
  phone_number_verified: boolean
  sub: string
  token_use: 'id'
}

class AuthService implements StandardAuthService {

  private authenticationAPI!: AuthenticationApi
  private localStorage: LocalStorageService
  private authenticationData: ReturnType<LocalStorageService['getAuthenticationData']>
  private onTokensUpdatedCallback: ((tokens: CognitoAuthenticationResult | null) => void) | null = null

  constructor () {
    this.localStorage = new LocalStorageService()
    this.updateAuthenticationData(this.localStorage.getAuthenticationData())
    this.authenticationData = this.localStorage.getAuthenticationData()
    const config = new Configuration({
      basePath: import.meta.env.VITE_BASE_URL || '',
      accessToken: this.authenticationData?.accessToken ?? undefined,
    })
    this.authenticationAPI = new AuthenticationApi(config)
  }

  onTokensUpdated = (callback: typeof this.onTokensUpdatedCallback) => {
    this.onTokensUpdatedCallback = callback
  }

  emitOnTokensUpdated = (authenticationResult: CognitoAuthenticationResult | null) => {
    if (this.onTokensUpdatedCallback) {
      this.onTokensUpdatedCallback(authenticationResult)
    }
  }

  authenticateSilently = async () => {
    if (this.authenticationData?.accessToken && this.tokenIsExpired(this.authenticationData.accessToken)) {
      const res = await this.refreshTokenAuth()
      if (!res) {
        this.logout()
        return null
      }
      return res.authenticationResult ?? null
    }
    return this.authenticationData
  }

  passwordLogin = async (email: string, password: string, rememberMe: boolean, recaptchaToken: string) => {
    const res = await this.authenticationAPI.loginAccount({
      loginAccountRequest: {
        username: email,
        password: password,
        retrieveDeviceKeys: rememberMe,
        recaptchaToken,
      }
    })
    if (res.authenticationResult) {
      this.localStorage.updateAuthenticationData(res.authenticationResult)
      this.updateAuthenticationData(res.authenticationResult)
    }
    return res
  }

  respondToSMSToken = async (sub: string, code: string, session: string) => {
    const res = await this.authenticationAPI.respondToAuthChallenge({
      respondToAuthChallengeRequest: {
        username: sub,
        challengeType: 'SMS_MFA',
        challengeResponses: {
          USERNAME: sub,
          SMS_MFA_CODE: code,
        },
        session: session
      }
    })
    if (res.authenticationResult) {
      this.updateAuthenticationData(res.authenticationResult)
    }
    return res
  }

  respondToSoftwareToken = async (sub: string, code: string, session: string) => {
    const res = await this.authenticationAPI.respondToAuthChallenge({
      respondToAuthChallengeRequest: {
        username: sub,
        challengeType: 'SOFTWARE_TOKEN_MFA',
        challengeResponses: {
          USERNAME: sub,
          SOFTWARE_TOKEN_MFA_CODE: code,
        },
        session: session
      }
    })
    if (res.authenticationResult) {
      this.updateAuthenticationData(res.authenticationResult)
    }
    return res
  }

  exchangeCodeForTokens = async (code: string) => {
    const res = await this.authenticationAPI.exchangeCodeForTokens({
      exchangeCodeForTokensRequest: { code }
    })
    if (res) {
      this.updateAuthenticationData(res)
    }
    return res
  }

  confirmAccount = async (username: string, password: string, token: string, firstName: string, lastName: string) => {
    const res = await this.authenticationAPI.confirmAccount({
      confirmAccountRequest: {
        customUsername: username,
        password,
        token,
        firstName,
        lastName,
      }
    })
    if (res.authenticationResult) {
      this.updateAuthenticationData(res.authenticationResult)
    }
  }

  resetPassword = async (password: string, token: string, rememberMe: boolean, recaptchaToken: string) => {
    const res = await this.authenticationAPI.resetPasswordWithToken({
      resetPasswordWithTokenRequest: {
        password,
        token,
        retrieveDeviceKeys: rememberMe,
        recaptchaToken,
      }
    })
    if (res.authenticationResult) {
      this.updateAuthenticationData(res.authenticationResult)
    }
  }

  refreshTokenAuth = async () => {
    const accessTokenPayload = this.getTokenPayload(this.authenticationData?.accessToken ?? '')
    const refreshToken = this.getRefreshToken()
    if (!accessTokenPayload || !refreshToken) {
      return
    }
    const res = await this.authenticationAPI.refreshTokens({
      refreshTokensRequest: {
        sub: accessTokenPayload.username,
        refreshToken: refreshToken,
        deviceKey: this.getDeviceKey() ?? '',
      }
    })
    this.updateAuthenticationData(res.authenticationResult ?? null)
    return res
  }

  logout = () => {
    this.updateAuthenticationData(null)
  }

  getAccessToken = async () => {
    if (!this.authenticationData || !this.authenticationData.accessToken) {
      return null
    }
    if (!this.tokenIsExpired(this.authenticationData.accessToken)) {
      return this.authenticationData.accessToken
    }
    const newTokens = await this.refreshTokenAuth()
    if (!newTokens?.authenticationResult) {
      this.logout()
      return null
    }
    return newTokens.authenticationResult.accessToken
  }

  getIdToken = async () => {
    if (!this.authenticationData || !this.authenticationData.idToken) {
      return null
    }
    if (!this.tokenIsExpired(this.authenticationData.idToken)) {
      return this.authenticationData.idToken
    }
    const newTokens = await this.refreshTokenAuth()
    if (!newTokens?.authenticationResult) {
      this.logout()
      return null
    }
    return newTokens.authenticationResult.idToken
  }

  getRefreshToken = () => {
    if (!this.authenticationData || !this.authenticationData.refreshToken) {
      return null
    }
    return this.authenticationData.refreshToken
  }

  getDeviceKey = () => {
    if (!this.authenticationData || !this.authenticationData.newDeviceMetadata?.deviceKey) {
      return null
    }
    return this.authenticationData.newDeviceMetadata.deviceKey
  }

  getAccessTokenPayload = async (): Promise<AccessTokenPayload | null> => {
    const token = await this.getAccessToken()
    if (!token) {
      return null
    }
    return this.getTokenPayload(token)
  }

  getIdTokenPayload = async (): Promise<IdTokenPayload | null> => {
    const token = await this.getIdToken()
    if (!token) {
      return null
    }
    return this.getTokenPayload(token)
  }

  private getTokenPayload = (token: string) => {
    return JSON.parse(atob(token.split('.')[1]))
  }

  private tokenIsExpired = (token: string): boolean => {
    const expiry = this.getTokenPayload(token).exp
    return (Math.floor((new Date()).getTime() / 1000)) >= expiry
  }

  private updateAuthenticationData = (data: CognitoAuthenticationResult | null) => {
    this.localStorage.updateAuthenticationData(data)
    this.authenticationData = data
    this.emitOnTokensUpdated(data)
  }
}

let authService: AuthService | null = null

export const getAuthService = () => {
  if (authService) {
    return authService
  }
  const _authService = new AuthService()
  authService = _authService
  return authService
}

export type { AuthService }